import React, { useMemo } from 'react'
import { useGetLinkedAccounts } from '../../../api/agencies/useGetLinkedAccounts'
import { useGetUser } from '../../../api/auth/useGetUser'
import { Outlet, Route, Routes, useParams } from 'react-router'
import ConnectedVendorAccount from '../../../components/VendorAccount/ConnectedVendorAccount'
import NotConnectedVendorAccount from '../../../components/VendorAccount/NotConnectedVendorAccount'
import EditVendorAccount from '../../../components/VendorAccount/EditVendorAccount'
import DeleteVendorAccount from '../../../components/VendorAccount/DeleteVendorAccount'
import PageHeader from '../../../components/PageHeader/PageHeader'
import { ReactComponent as LinkedPlatformsIcon } from '../../../images/icons/linked_accounts.svg'
import { Toast } from '@demandlocal/components'

const AccountsTemplate = (): JSX.Element => {
  const { data: vendors } = useGetLinkedAccounts()
  const { data: user, isLoading: loadingUser } = useGetUser()
  const { agency_id } = useParams()

  const connectedAccounts = useMemo(() => vendors?.filter((v) => v.connected), [vendors])
  const notConnectedAccounts = useMemo(
    () => vendors?.filter((v) => !v.connected),
    [vendors],
  )

  const hasConnectedAccounts = useMemo(
    () => connectedAccounts && connectedAccounts?.length,
    [connectedAccounts],
  )

  const hasNonConnectedAccounts = useMemo(
    () => notConnectedAccounts && notConnectedAccounts?.length,
    [notConnectedAccounts],
  )

  const canConnect = useMemo(() => {
    return import.meta.env?.VITE_IS_DEV
      ? true
      : !loadingUser &&
          !(user?.email.includes('@demandlocal.com') && Number(agency_id) !== 1)
  }, [user?.email, agency_id, loadingUser])

  return (
    <div className="flex flex-col">
      <PageHeader
        title={'Linked Platforms'}
        icon={
          <div className="flex justify-center items-center w-10 h-10 rounded-full bg-black">
            <LinkedPlatformsIcon className="stroke-primary-white fill-primary-white" />
          </div>
        }
      />
      <div className="py-5">
        {!canConnect && (
          <div className="py-6 px-10">
            <Toast
              className={'font-bold'}
              variant={'error'}
              text="You are not allowed to connect your demandlocal account under this
                  agency"
            />
          </div>
        )}
        {hasConnectedAccounts ? (
          <div className="flex flex-col gap-2 divide-y-2 px-10">
            <div className="grid grid-cols-4 items-center">
              <span className="font-bold text-lg">Platform</span>
              <span className="font-bold text-lg">Activated By</span>
              <span className="font-bold text-lg">Activation Date</span>
              <span className="font-bold text-lg">Actions</span>
            </div>
            {connectedAccounts?.map((vendor) => (
              <ConnectedVendorAccount key={vendor.id} vendor={vendor} />
            ))}
          </div>
        ) : (
          <></>
        )}
        {hasNonConnectedAccounts ? (
          <div className="px-10 py-5">
            <span className="font-bold text-xl">Not Linked</span>
            <div className="flex flex-col gap-2 divide-y-2 border-b-2">
              {notConnectedAccounts?.map((vendor) => (
                <NotConnectedVendorAccount
                  key={vendor.id}
                  vendor={vendor}
                  disabled={!canConnect}
                />
              ))}
            </div>
          </div>
        ) : (
          <></>
        )}
        <Outlet />
      </div>
    </div>
  )
}

export default function Accounts(): JSX.Element {
  return (
    <Routes>
      <Route path="*" element={<AccountsTemplate />}>
        <Route path=":account_id/edit" element={<EditVendorAccount />} />
        <Route path=":account_id/delete" element={<DeleteVendorAccount />} />
      </Route>
    </Routes>
  )
}
